// alignmentItems — normalise KYRS tasks, TKNE project tasks and METIS agenda
// instances into the Alignment Engine's WorkItems (Compass Sector Fill spec,
// 18 Oct 2026 §1). Pure, so the linkage rules are testable without a DB.
// LINKED: an Aim ref, a project, a task directly under an Aim task, or a task
// whose title IS one of the sector's Aims. Leaf tasks only: a task with
// subtasks never counts itself.
import { SECTORS, type Sector } from '../domain/olmps';
import { effectiveAims, type OracleSpine } from './oracle-spine-data';
import type { WorkItem } from './alignment';
import type { ProjectLeaf } from './sectorProgress';

export const asSector = (v: unknown): Sector | null => (typeof v === 'string' && v.length === 1 && 'SHAPE'.includes(v) ? (v as Sector) : null);
export const keyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** tasks.scheduled ('yyyy-MM-dd', 'yyyy-MM-dd HH:mm', 'today'…) → Date — same reading as lib/autoSchedule.parseScheduled. */
function parseDue(v: string | null): Date | null {
  if (!v) return null;
  const full = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v);
  if (full) return new Date(+full[1], +full[2] - 1, +full[3], +full[4], +full[5]);
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (day) return new Date(+day[1], +day[2] - 1, +day[3]);
  const off = { today: 0, tomorrow: 1, yesterday: -1 }[v.toLowerCase().trim()];
  if (off == null) return null;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + off);
  return d;
}
const dayOfIso = (v: string | null) => {
  const d = v ? new Date(v) : null;
  return d && Number.isFinite(d.getTime()) ? keyOf(d) : null;
};
/** "8:00 AM" / "14:30" → minutes after midnight, null when no time is set. */
function clockMinutes(v: string | null): number | null {
  const m = /^\s*(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?\s*$/i.exec(v ?? '');
  if (!m) return null;
  let h = +m[1];
  const ap = m[3]?.[0]?.toLowerCase();
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  return h * 60 + +m[2];
}
const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();


export interface AlignmentRaw {
  tasks: { id: string; title: string | null; scheduled: string | null; done: boolean | null; unlisted: boolean | null; sector: string | null; list_id: string | null; design_id: string | null; aim_ref: string | null; parent_task_id: string | null; updated_at: string | null }[];
  agenda: { id: string; date: string; time: string | null; completed: boolean | null; sector: string | null; task_id: string | null; design_id: string | null; aim_ref: string | null; text: string | null; updated_at: string | null }[];
  lists: Record<string, Sector>;
  designs: Record<string, Sector>;
  /** Active projects with their leaf tasks already parsed (sectorProgress.projectLeaves). */
  projects: { id: string; sector: Sector; leaves: ProjectLeaf[] }[];
  spine: OracleSpine | null;
}

/** Normalise the raw rows into WorkItems. */
export function workItemsOf(raw: AlignmentRaw): WorkItem[] {
  const items: WorkItem[] = [];
  const aimTexts = Object.fromEntries(
    SECTORS.map((s) => [s, new Set(raw.spine?.[s] ? effectiveAims(raw.spine[s]).map((x) => norm(x.label)).filter(Boolean) : [])]),
  ) as Record<Sector, Set<string>>;
  const isAim = (sector: Sector, text: string | null) => aimTexts[sector].has(norm(text));

  // KYRS tasks — leaf tasks only.
  const byId = new Map(raw.tasks.map((t) => [t.id, t]));
  const parents = new Set(raw.tasks.map((t) => t.parent_task_id).filter(Boolean) as string[]);
  const taskLinked = new Map<string, boolean>();
  for (const t of raw.tasks) {
    if (t.unlisted || parents.has(t.id)) continue;
    const sector = asSector(t.sector) ?? (t.design_id ? raw.designs[t.design_id] : null) ?? (t.list_id ? raw.lists[t.list_id] : null);
    if (!sector) continue;
    const parent = t.parent_task_id ? byId.get(t.parent_task_id) : undefined;
    const linked = !!(t.aim_ref || t.design_id || parent?.aim_ref || parent?.design_id || isAim(sector, t.title) || (parent && isAim(sector, parent.title)));
    taskLinked.set(t.id, linked);
    const when = parseDue(t.scheduled);
    const timed = !!t.scheduled && /\d{1,2}:\d{2}/.test(t.scheduled);
    items.push({
      id: `task:${t.id}`,
      sector,
      date: when ? keyOf(when) : null,
      dueAt: when && timed ? when.getTime() : null,
      done: !!t.done,
      doneDate: t.done ? dayOfIso(t.updated_at) : null,
      linked,
    });
  }
  // TKNE project tasks — each project's leaf Directions, linked through the project.
  for (const p of raw.projects) {
    for (const leaf of p.leaves) {
      const date = leaf.dueDate && /^\d{4}-\d{2}-\d{2}/.test(leaf.dueDate) ? leaf.dueDate.slice(0, 10) : null;
      items.push({ id: `step:${p.id}:${leaf.id}`, sector: p.sector, date, done: leaf.done, doneDate: leaf.done ? date : null, linked: true });
    }
  }
  // METIS agenda instances (recurring occurrences are one instance per date).
  for (const a of raw.agenda) {
    if (!a.text || (a.task_id && taskLinked.has(a.task_id))) continue; // the task already counts — never twice
    const sector = asSector(a.sector) ?? (a.design_id ? raw.designs[a.design_id] : null);
    if (!sector) continue;
    const mins = clockMinutes(a.time);
    const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(a.date);
    const dueAt = day && mins != null ? new Date(+day[1], +day[2] - 1, +day[3], Math.floor(mins / 60), mins % 60).getTime() : null;
    const linked = !!(a.aim_ref || a.design_id || isAim(sector, a.text) || (a.task_id && byId.get(a.task_id)?.aim_ref));
    items.push({ id: `agenda:${a.id}`, sector, date: a.date.slice(0, 10), dueAt, done: !!a.completed, doneDate: a.completed ? dayOfIso(a.updated_at) : null, linked });
  }
  return items;
}

